// Rating CETS ad "alberi" (stile Guida Michelin): più alberi = maggiore adesione
// alle buone pratiche CETS. Usato nel CRM stakeholder e nelle vetrine pubbliche.
// Può ricevere direttamente un oggetto `rating` oppure un `ownerId` (self-fetch).
import React, { useEffect, useState } from 'react';
import { TreePine, Info } from 'lucide-react';
import { Popover, PopoverTrigger, PopoverContent } from '@/components/ui/popover';
import { cetsSelfService } from '@/services/cets.service';

const SIZES = {
  sm: { tree: 'h-4 w-4', label: 'text-xs', gap: 'gap-0.5' },
  md: { tree: 'h-5 w-5', label: 'text-sm', gap: 'gap-1' },
  lg: { tree: 'h-7 w-7', label: 'text-base', gap: 'gap-1' },
};

const Trees = ({ level, max = 3, size = 'md' }) => {
  const s = SIZES[size] || SIZES.md;
  return (
    <div className={`flex items-center ${s.gap}`} role="img" aria-label={`${level} alberi CETS su ${max}`}>
      {Array.from({ length: max }).map((_, i) => (
        <TreePine
          key={i}
          className={`${s.tree} transition-colors ${i < level ? 'text-emerald-600 dark:text-emerald-400' : 'text-muted-foreground/25'}`}
          strokeWidth={i < level ? 2.2 : 1.8}
          fill={i < level ? 'currentColor' : 'none'}
          fillOpacity={i < level ? 0.14 : 0}
        />
      ))}
    </div>
  );
};

export const CetsTrees = ({
  rating: ratingProp,
  ownerId,
  size = 'md',
  showLabel = true,
  showScore = false,
  className = '',
  'data-testid': testId = 'cets-trees',
}) => {
  const [rating, setRating] = useState(ratingProp || null);

  useEffect(() => {
    let alive = true;
    if (ratingProp) { setRating(ratingProp); return; }
    if (ownerId) {
      cetsSelfService.publicRating(ownerId)
        .then((r) => { if (alive) setRating(r); })
        .catch(() => { if (alive) setRating(null); });
    }
    return () => { alive = false; };
  }, [ratingProp, ownerId]);

  // In vetrina mostriamo il badge SOLO quando il questionario è completo (published).
  if (!rating || !rating.published) return null;

  const max = rating.trees_max || 3;
  return (
    <div className={`inline-flex items-center gap-2 rounded-full border border-emerald-600/20 bg-emerald-500/5 px-3 py-1.5 ${className}`} data-testid={testId}>
      <Trees level={rating.level} max={max} size={size} />
      {showLabel && (
        <span className={`font-medium text-emerald-700 dark:text-emerald-300 ${(SIZES[size] || SIZES.md).label}`}>
          {rating.level_label}
        </span>
      )}
      {showScore && typeof rating.score === 'number' && (
        <span className={`text-muted-foreground ${(SIZES[size] || SIZES.md).label}`}>· {rating.score}/100</span>
      )}
      <Popover>
        <PopoverTrigger asChild>
          <button
            type="button"
            className="inline-flex items-center text-emerald-700/70 transition-colors hover:text-emerald-700 dark:text-emerald-300/70"
            aria-label="Cos'è la valutazione CETS"
            data-testid="cets-trees-info"
          >
            <Info className="h-4 w-4" />
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-72 text-sm" align="start">
          <p className="mb-1 flex items-center gap-1.5 font-display font-semibold text-emerald-700 dark:text-emerald-300">
            <TreePine className="h-4 w-4" />Valutazione CETS
          </p>
          <p className="text-muted-foreground">{rating.disclaimer}</p>
        </PopoverContent>
      </Popover>
    </div>
  );
};

export default CetsTrees;
